import React, { useState } from 'react';
import {
  Plus, X, Search, ChevronDown, ChevronUp, Zap, Target,
  Building2, Globe2, Users, DollarSign, Cpu, TrendingUp, Info
} from 'lucide-react';

// ── Type ──────────────────────────────────────────────────────────────────────
export interface AdvancedCriteria {
  mode: 'BUYER' | 'PARTNER';
  partnerType?: string;
  territory?: string;
  targetIndustry: string;
  region: string;
  sizeMin: number;
  sizeMax: number;
  revenueRange: string[];
  keywords: string[];
  techStack: string[];
  intentTopics: string[];
  buyingSignals: string[];
  maxResults: number;
  connectors?: Record<string, string>;
}

const PARTNER_TYPES = [
  { value: '', label: 'Any partner type' },
  { value: 'RESELLER', label: 'Reseller' },
  { value: 'SYSTEM_INTEGRATOR', label: 'System Integrator' },
  { value: 'DISTRIBUTOR', label: 'Distributor' },
  { value: 'VAR', label: 'VAR' },
  { value: 'CONSULTANT', label: 'Consultant' },
];

interface Props {
  onSubmit: (criteria: AdvancedCriteria) => void;
  loading: boolean;
  disabled?: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const INDUSTRIES = [
  'Manufacturing', 'Automotive', 'Pharmaceuticals', 'Food & Beverage',
  'Electronics', 'Chemicals', 'Textiles', 'Steel & Metals',
  'Logistics & Supply Chain', 'Oil & Gas', 'Energy', 'Plastics & Rubber',
  'Packaging', 'Paper', 'Ceramics & Glass', 'Printing',
  'IT Services', 'Aerospace & Defence',
];

const REGIONS = [
  'India', 'Middle East', 'Southeast Asia', 'GCC', 'UAE',
  'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman',
  'Egypt', 'Turkey', 'South Africa', 'Nigeria', 'Kenya', 'Morocco',
  'Malaysia', 'Indonesia', 'Bangladesh', 'Sri Lanka', 'Pakistan',
  'Singapore', 'Thailand', 'Vietnam', 'Philippines',
];

const SIZE_PRESETS = [
  { label: 'SME',        min: 50,   max: 250  },
  { label: 'Mid-Market', min: 250,  max: 1000 },
  { label: 'Enterprise', min: 1000, max: 5000 },
  { label: 'All',        min: 50,   max: 5000 },
];

const REVENUE_OPTIONS = [
  { value: '5M-10M',    label: '$5M – $10M'   },
  { value: '10M-25M',   label: '$10M – $25M'  },
  { value: '25M-75M',   label: '$25M – $75M'  },
  { value: '75M-200M',  label: '$75M – $200M' },
  { value: '200M-500M', label: '$200M – $500M'},
  { value: '500M-1B',   label: '$500M – $1B'  },
];

// MES/Smart Factory specific tech stack signals — these are Explorium tech values
const TECH_PRESETS = [
  { label: 'SAP ERP',         value: 'SAP ERP' },
  { label: 'Oracle ERP',      value: "Oracle's JD Edwards World" },
  { label: 'Ignition SCADA',  value: 'Ignition by Inductive Automation' },
  { label: 'Wonderware',      value: 'Wonderware' },
  { label: 'Siemens',         value: 'Siemens EDA' },
];

// Intent topics — Explorium business_intent_topics values
const INTENT_PRESETS = [
  { label: 'ERP Implementation', value: 'enterprise: erp implementation',       icon: '🔧' },
  { label: 'Cloud ERP',          value: 'cloud: cloud erp',                     icon: '☁️' },
  { label: 'MES / Factory Auto', value: 'manufacturing: cellular manufacturing', icon: '🏭' },
  { label: 'Rockwell Automation',value: 'manufacturing: rockwell automation',    icon: '⚙️' },
  { label: 'NetSuite ERP',       value: 'enterprise: netsuite erp',             icon: '📊' },
  { label: 'Messaging Platform', value: 'messaging: integrated messaging',       icon: '💬' },
];

// Buying signals — keywords used in Tavily web search
const SIGNAL_PRESETS = [
  { label: 'Hiring Automation Engineers', value: 'hiring automation engineer'        },
  { label: 'Digital Transformation',      value: 'digital transformation initiative' },
  { label: 'MES Implementation',          value: 'MES implementation project'        },
  { label: 'Smart Factory',               value: 'smart factory Industry 4.0'        },
  { label: 'OEE Improvement',             value: 'OEE improvement manufacturing'     },
  { label: 'ERP Upgrade',                 value: 'ERP upgrade implementation 2025'   },
  { label: 'SCADA Modernisation',         value: 'SCADA modernisation upgrade'       },
  { label: 'Digital Twin',                value: 'digital twin manufacturing'        },
  { label: 'IIoT / Industry 4.0',         value: 'IIoT Industry 4.0 deployment'     },
  { label: 'Plant Expansion',             value: 'plant expansion greenfield'        },
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function ProspectCriteriaForm({ onSubmit, loading, disabled = false }: Props) {
  const [criteria, setCriteria] = useState<AdvancedCriteria>({
    mode:           'BUYER',
    partnerType:    '',
    territory:      '',
    targetIndustry: 'Manufacturing',
    region:         'India',
    sizeMin:        100,
    sizeMax:        1000,
    revenueRange:   [],
    keywords:       [],
    techStack:      [],
    intentTopics:   [],
    buyingSignals:  ['hiring automation engineer', 'MES implementation project'],
    maxResults:     25,
  });

  const [kwInput, setKwInput] = useState('');
  const [techInput, setTechInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const toggle = <K extends keyof AdvancedCriteria>(key: K, value: string) => {
    const arr = criteria[key] as string[];
    setCriteria(p => ({
      ...p,
      [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value],
    }));
  };

  const addCustom = (key: 'keywords' | 'techStack', input: string, setInput: (v: string) => void) => {
    const val = input.trim();
    if (!val) return;
    const arr = criteria[key] as string[];
    if (!arr.includes(val)) setCriteria(p => ({ ...p, [key]: [...arr, val] }));
    setInput('');
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!criteria.targetIndustry) e.targetIndustry = 'Required';
    if (!criteria.region)         e.region         = 'Required';
    if (criteria.sizeMin <= 0)    e.sizeMin         = 'Must be > 0';
    if (criteria.sizeMax < criteria.sizeMin) e.sizeMax = 'Must be ≥ min';
    if (criteria.mode !== 'PARTNER'
      && criteria.buyingSignals.length === 0 && criteria.keywords.length === 0 && criteria.intentTopics.length === 0)
      e.signals = 'Select at least one buying signal, intent topic, or keyword';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit(criteria);
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const S = {
    section: 'space-y-3',
    label:   'flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2',
    input:   'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors',
    pill:    (active: boolean) => `inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
      active
        ? 'bg-primary text-primary-foreground border-primary shadow-sm'
        : 'bg-muted/50 text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
    }`,
    err: 'text-xs text-red-500 mt-1',
  };

  const activeCount = criteria.buyingSignals.length + criteria.intentTopics.length
    + criteria.techStack.length + criteria.keywords.length + criteria.revenueRange.length;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* ── Mode: Buyer vs Partner ── */}
      <div>
        <label className={S.label}><Target className="h-3 w-3" /> Prospecting Mode</label>
        <div className="grid grid-cols-2 gap-2">
          <button type="button"
            onClick={() => setCriteria(p => ({ ...p, mode: 'BUYER' }))}
            className={`rounded-lg border px-3 py-2 text-left transition-all ${criteria.mode === 'BUYER' ? 'border-primary bg-primary/10 shadow-sm' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
            <div className="text-sm font-semibold flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Buyer</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Manufacturers to sell MES to</div>
          </button>
          <button type="button"
            onClick={() => setCriteria(p => ({ ...p, mode: 'PARTNER' }))}
            className={`rounded-lg border px-3 py-2 text-left transition-all ${criteria.mode === 'PARTNER' ? 'border-primary bg-primary/10 shadow-sm' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
            <div className="text-sm font-semibold flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Partner</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Resellers / SIs to recruit</div>
          </button>
        </div>
      </div>

      {/* ── Partner controls (partner mode only) ── */}
      {criteria.mode === 'PARTNER' && (
        <div className="grid grid-cols-2 gap-4 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div>
            <label className={S.label}><Users className="h-3 w-3" /> Partner Type</label>
            <select className={S.input} value={criteria.partnerType || ''}
              onChange={e => setCriteria(p => ({ ...p, partnerType: e.target.value }))}>
              {PARTNER_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className={S.label}><Globe2 className="h-3 w-3" /> Territory (optional)</label>
            <input type="text" className={S.input} placeholder="e.g. South India, GCC"
              value={criteria.territory || ''}
              onChange={e => setCriteria(p => ({ ...p, territory: e.target.value }))} />
          </div>
          <p className="col-span-2 text-[11px] text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3" /> Partner mode finds channel firms (resellers, SIs, distributors) and drafts partnership outreach. Buying-signal filters are optional here.
          </p>
        </div>
      )}

      {/* ── Row 1: Industry + Region ── */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={S.label}><Building2 className="h-3 w-3" /> {criteria.mode === 'PARTNER' ? 'Target Market / Vertical' : 'Industry'}</label>
          <select className={S.input} value={criteria.targetIndustry}
            onChange={e => setCriteria(p => ({ ...p, targetIndustry: e.target.value }))}>
            {INDUSTRIES.map(o => <option key={o}>{o}</option>)}
          </select>
          {errors.targetIndustry && <p className={S.err}>{errors.targetIndustry}</p>}
        </div>
        <div>
          <label className={S.label}><Globe2 className="h-3 w-3" /> Region</label>
          <select className={S.input} value={criteria.region}
            onChange={e => setCriteria(p => ({ ...p, region: e.target.value }))}>
            {REGIONS.map(o => <option key={o}>{o}</option>)}
          </select>
          {errors.region && <p className={S.err}>{errors.region}</p>}
        </div>
      </div>

      {/* ── Row 2: Company Size ── */}
      <div>
        <label className={S.label}><Users className="h-3 w-3" /> Company Size</label>
        <div className="flex items-center gap-2 flex-wrap">
          {SIZE_PRESETS.map(p => (
            <button key={p.label} type="button"
              onClick={() => setCriteria(c => ({ ...c, sizeMin: p.min, sizeMax: p.max }))}
              className={S.pill(criteria.sizeMin === p.min && criteria.sizeMax === p.max)}>
              {p.label} <span className="opacity-70">{p.min}–{p.max}</span>
            </button>
          ))}
          <div className="flex items-center gap-1.5 ml-1">
            <input type="number" min={1} className="w-20 rounded-lg border border-border bg-background px-2 py-1 text-xs text-center"
              value={criteria.sizeMin}
              onChange={e => setCriteria(p => ({ ...p, sizeMin: parseInt(e.target.value) || 1 }))} />
            <span className="text-xs text-muted-foreground">to</span>
            <input type="number" min={1} className="w-20 rounded-lg border border-border bg-background px-2 py-1 text-xs text-center"
              value={criteria.sizeMax}
              onChange={e => setCriteria(p => ({ ...p, sizeMax: parseInt(e.target.value) || 1 }))} />
            <span className="text-xs text-muted-foreground">employees</span>
          </div>
        </div>
        {errors.sizeMin && <p className={S.err}>{errors.sizeMin}</p>}
        {errors.sizeMax && <p className={S.err}>{errors.sizeMax}</p>}
      </div>

      {/* ── Buying Signals ── */}
      <div>
        <label className={S.label}>
          <TrendingUp className="h-3 w-3" /> Buying Signals
          <span className="ml-1 text-[10px] font-normal normal-case text-muted-foreground">(powers Tavily web search)</span>
        </label>
        <div className="flex flex-wrap gap-1.5">
          {SIGNAL_PRESETS.map(s => (
            <button key={s.value} type="button"
              onClick={() => toggle('buyingSignals', s.value)}
              className={S.pill(criteria.buyingSignals.includes(s.value))}>
              {criteria.buyingSignals.includes(s.value) && <Zap className="h-2.5 w-2.5" />}
              {s.label}
            </button>
          ))}
        </div>
        {errors.signals && <p className={S.err}>{errors.signals}</p>}
      </div>

      {/* ── Intent Topics ── */}
      <div>
        <label className={S.label}>
          <Target className="h-3 w-3" /> Purchase Intent Topics
          <span className="ml-1 text-[10px] font-normal normal-case text-muted-foreground">(Explorium intent signals)</span>
        </label>
        <div className="flex flex-wrap gap-1.5">
          {INTENT_PRESETS.map(t => (
            <button key={t.value} type="button"
              onClick={() => toggle('intentTopics', t.value)}
              className={S.pill(criteria.intentTopics.includes(t.value))}>
              <span>{t.icon}</span>{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Advanced toggle ── */}
      <div>
        <button type="button"
          onClick={() => setShowAdvanced(v => !v)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          {showAdvanced ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          Advanced filters
          {activeCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">{activeCount}</span>
          )}
        </button>

        {showAdvanced && (
          <div className="mt-4 space-y-5 rounded-xl border border-border bg-muted/20 p-4">

            {/* Revenue */}
            <div>
              <label className={S.label}><DollarSign className="h-3 w-3" /> Annual Revenue</label>
              <div className="flex flex-wrap gap-1.5">
                {REVENUE_OPTIONS.map(r => (
                  <button key={r.value} type="button"
                    onClick={() => toggle('revenueRange', r.value)}
                    className={S.pill(criteria.revenueRange.includes(r.value))}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tech Stack */}
            <div>
              <label className={S.label}>
                <Cpu className="h-3 w-3" /> Technology Stack
                <span className="ml-1 text-[10px] font-normal normal-case text-muted-foreground">(Explorium tech filter)</span>
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {TECH_PRESETS.map(t => (
                  <button key={t.value} type="button"
                    onClick={() => toggle('techStack', t.value)}
                    className={S.pill(criteria.techStack.includes(t.value))}>
                    {t.label}
                  </button>
                ))}
                {criteria.techStack
                  .filter(v => !TECH_PRESETS.find(p => p.value === v))
                  .map(v => (
                    <button key={v} type="button"
                      onClick={() => toggle('techStack', v)}
                      className={S.pill(true)}>
                      {v} <X className="h-2.5 w-2.5" />
                    </button>
                  ))}
              </div>
              <div className="flex gap-2">
                <input type="text" className={`${S.input} text-xs`}
                  placeholder="Add tech (e.g. Infor, Epicor, Plex...)"
                  value={techInput}
                  onChange={e => setTechInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom('techStack', techInput, setTechInput); } }} />
                <button type="button" onClick={() => addCustom('techStack', techInput, setTechInput)}
                  className="px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Custom Keywords */}
            <div>
              <label className={S.label}>
                <Search className="h-3 w-3" /> Custom Keywords
                <span className="ml-1 text-[10px] font-normal normal-case text-muted-foreground">(site content search)</span>
              </label>
              <div className="flex gap-2 mb-2">
                <input type="text" className={`${S.input} text-xs`}
                  placeholder="e.g. lean manufacturing, TPM, Kaizen, OEE..."
                  value={kwInput}
                  onChange={e => setKwInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom('keywords', kwInput, setKwInput); } }} />
                <button type="button" onClick={() => addCustom('keywords', kwInput, setKwInput)}
                  className="px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {criteria.keywords.map(kw => (
                  <span key={kw} className={S.pill(true)}
                    onClick={() => toggle('keywords', kw)}>
                    {kw} <X className="h-2.5 w-2.5 cursor-pointer" />
                  </span>
                ))}
              </div>
            </div>

            {/* Max Results */}
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-muted-foreground">Max results</label>
              {[10, 25, 50].map(n => (
                <button key={n} type="button"
                  onClick={() => setCriteria(p => ({ ...p, maxResults: n }))}
                  className={S.pill(criteria.maxResults === n)}>{n}</button>
              ))}
              <input type="number" min={1} max={50}
                className="w-16 rounded-lg border border-border bg-background px-2 py-1 text-xs text-center"
                value={criteria.maxResults}
                onChange={e => setCriteria(p => ({ ...p, maxResults: parseInt(e.target.value) || 25 }))} />
              {errors.maxResults && <p className={S.err}>{errors.maxResults}</p>}
            </div>
          </div>
        )}
      </div>

      {/* ── Summary chip bar ── */}
      {(criteria.buyingSignals.length > 0 || criteria.intentTopics.length > 0 || criteria.techStack.length > 0) && (
        <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
          <p className="text-[10px] font-semibold text-primary uppercase tracking-wider mb-1.5">Active filters</p>
          <div className="flex flex-wrap gap-1">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-background border border-border text-muted-foreground">
              {criteria.targetIndustry} · {criteria.region} · {criteria.sizeMin}–{criteria.sizeMax} employees
            </span>
            {criteria.buyingSignals.map(s => (
              <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400">
                ⚡ {SIGNAL_PRESETS.find(p => p.value === s)?.label || s}
              </span>
            ))}
            {criteria.intentTopics.map(t => (
              <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400">
                🎯 {INTENT_PRESETS.find(p => p.value === t)?.label || t}
              </span>
            ))}
            {criteria.techStack.map(t => (
              <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-400">
                💻 {TECH_PRESETS.find(p => p.value === t)?.label || t}
              </span>
            ))}
            {criteria.revenueRange.map(r => (
              <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400">
                💰 {REVENUE_OPTIONS.find(o => o.value === r)?.label || r}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Submit ── */}
      <button type="submit" disabled={loading || disabled}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
        {loading ? (
          <><span className="h-4 w-4 rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground animate-spin" />Starting run...</>
        ) : (
          <><Zap className="h-4 w-4" />Run Vibe Prospecting{criteria.mode === 'PARTNER' ? ' · Partners' : ''}</>
        )}
      </button>
    </form>
  );
}
